import * as Sentry from '@sentry/react'
import { sentry, app } from '@/config'

// Initialize Sentry error tracking
export function initSentry() {
  // Only initialize Sentry if enabled and DSN is provided
  if (!sentry.enabled || !sentry.dsn) {
    if (app.isProduction && !sentry.dsn) {
      console.warn('Sentry DSN not configured in production')
    }
    return
  }

  Sentry.init({
    dsn: sentry.dsn,
    environment: app.environment,
    release: app.version || `${Date.now()}`,
      
    // Performance monitoring
    tracesSampleRate: app.isProduction ? 0.1 : 1.0,
    
    // Session replay for debugging (only in development)
    replaysSessionSampleRate: app.isDevelopment ? 1.0 : 0,
    replaysOnErrorSampleRate: app.isProduction ? 1.0 : 0,
    
    // Error filtering
    beforeSend(event, hint) {
      // Filter out development errors
      if (app.isDevelopment) {
        console.error('Sentry Error:', hint.originalException || event)
      }
      
      // Don't send errors in development
      if (app.isDevelopment) {
        return null
      }
      
      return event
    },
    
    // Additional context
    initialScope: {
      tags: {
        component: 'urban-finch-admin',
        version: app.version,
      },
    },
      
    integrations: [
      Sentry.browserTracingIntegration(),
      Sentry.replayIntegration(),
    ],
  })
}

// Error boundary component for React errors
export const SentryErrorBoundary = Sentry.withErrorBoundary

// Hook for manual error reporting
export function useErrorReporting() {
  const captureError = (error: Error, context?: Record<string, unknown>) => {
    Sentry.withScope((scope) => {
      if (context) {
        scope.setContext('error_context', context)
      }
      Sentry.captureException(error)
    })
  }

  const captureMessage = (message: string, level: 'info' | 'warning' | 'error' = 'info') => {
    Sentry.captureMessage(message, level)
  }

  return { captureError, captureMessage }
}

